import type { DataOption } from '@/common.types'
import type {
	AnchorsUpsertModel,
	FilterRuleModel,
	ProductCatalogQueryResponseModel,
	SalesEntityModel,
} from '@/shared/generated'

import { useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { map, paths, pipe, prop, uniqBy } from 'lodash/fp'
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from 'lucide-react'
import { useForm, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import {
	useAnchorsProductCatalogQuery,
	useUpsertAnchorsMutation,
} from '@/entities/anchor'
import { useAssortmentListQuery } from '@/entities/assortment'
import { useSalesEntitiesQuery } from '@/entities/optimization-group'

import AsyncCombobox from '@/shared/components/AsyncCombobox'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { FormProvider, RHFSelect } from '@/shared/components/HookForm'
import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import {
	Field,
	FieldGroup,
	FieldLabel,
	FieldSeparator,
	FieldSet,
} from '@/shared/components/ui/field'
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import {
	InputGroup,
	InputGroupAddon,
	InputGroupInput,
} from '@/shared/components/ui/input-group'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/ui/select'
import { Skeleton } from '@/shared/components/ui/skeleton'
import useStep from '@/shared/hooks/useStep'
import { ampli } from '@/shared/lib/ampli'
import { queryClient } from '@/shared/lib/query-client'
import { processingMessage } from '@/shared/tools/message'
import { toOption } from '@/shared/tools/utils'

import { getAnchorTypesOptions, linesToProducts } from '../lib/helpers'
import { FormSchema } from '../model/contract'
import { AnchorProductTable } from './AnchorProductTable'

type AddSkuToAnchorAssortmentModalProps = {
	isOpen: boolean
	close(): void
}

export function AddSkuToAnchorAssortmentModal({
	isOpen,
	close,
}: AddSkuToAnchorAssortmentModalProps) {
	const { t } = useTranslation()

	const [currentStep, helpers] = useStep({ maxStep: 3 })
	const { canGoToPrevStep, canGoToNextStep, goToNextStep, goToPrevStep } =
		helpers
	const methods = useForm({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			mul: null,
			add: null,
		},
	})
	const upsertAnchorsMutation = useUpsertAnchorsMutation({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['anchor-assortment'],
			})
			close()
		},
	})
	const _handleSubmit = () => {
		ampli.anchoringTableCreateAnchorSave()
		const {
			anchorProduct,
			anchor_type,
			mainProduct,
			source_family_id,
			mul,
			add,
		} = methods.getValues()
		const payload: AnchorsUpsertModel = {
			anchors: [
				{
					sku: anchorProduct!.sku,
					optimization_group_id: '',
					sales_entity_id: anchorProduct!.sales_entity_id,
					[`${anchor_type}_anchor_sku`]: mainProduct!.sku,
					...(mul !== 0 && {
						[`${anchor_type}_anchor_mul`]: mul,
					}),
					...(add !== 0 && {
						[`${anchor_type}_anchor_add`]: add,
					}),
					[`${anchor_type}_source_family_id`]: source_family_id!,
				},
			],
		}
		toast.promise(upsertAnchorsMutation.mutateAsync(payload), {
			success: t('toast.entity.updated', {
				entity: t('entity.anchor_management', {
					defaultValue: 'Anchor management',
				}),
			}),
			error: t('fatal_error_title'),
			loading: processingMessage(t),
			id: 'anchoring_list_insert',
		})
	}
	const handleSubmit = methods.handleSubmit(_handleSubmit)

	return (
		<Modal open={isOpen} onOpenChange={close}>
			<ModalContent className={currentStep === 3 ? 'max-w-[952px]' : ''}>
				<ModalHeader>
					<ModalTitle>
						{t('anchoring_management.create_anchor_sku.modal.title')}
					</ModalTitle>
				</ModalHeader>
				<ErrorBoundary>
					<ModalBody className='h-[350px] space-y-4 overflow-y-auto py-4'>
						<FormProvider methods={methods} onSubmit={handleSubmit}>
							{currentStep === 1 && (
								<FieldGroup>
									<FieldSet>
										<FieldGroup>
											<ProductSelector />
											{/* Select anchor type */}
											<RHFSelect
												data-testid='modal-assign-to-anchor-management-select-anchor-type'
												name='anchor_type'
												className='w-full'
												placeholder={t(
													'anchoring_management.assign_to_modal.anchor_type'
												)}
												label={t('anchoring_type')}
												options={getAnchorTypesOptions(t)}
											/>
										</FieldGroup>
									</FieldSet>
								</FieldGroup>
							)}
							{currentStep === 2 && (
								<FieldGroup>
									<FieldSet>
										<FieldGroup>
											<Field>
												<FieldLabel>
													{t('anchoring_management.modal.select_sku.title')}
												</FieldLabel>
												<InputGroup>
													<InputGroupInput
														readOnly
														disabled
														value={methods.getValues('product.title')}
													/>
													<InputGroupAddon>
														<SearchIcon />
													</InputGroupAddon>
												</InputGroup>
											</Field>
											<SalesEntitySelector />
											<FieldSeparator />
											{/* Select main SKU */}
											<MainProductSelector />
											{/* Reselect anchor type */}
											<RHFSelect
												data-testid='modal-assign-to-anchor-management-select-anchor-type'
												name='anchor_type'
												className='w-full'
												placeholder={t(
													'anchoring_management.assign_to_modal.anchor_type'
												)}
												label={t('anchoring_type')}
												options={getAnchorTypesOptions(t)}
											/>
										</FieldGroup>
									</FieldSet>
								</FieldGroup>
							)}
							{currentStep === 3 && (
								<>
									<p className='mb-4 text-sm font-semibold'>
										{t(
											'anchoring_management.create_anchor.new_anchor_table.title',
											{
												anchor_type: methods.watch('anchor_type'),
												main_product: `${methods.watch('mainProduct.sku')} ${methods.watch('mainProduct.title')}`,
											}
										)}
									</p>
									<div className='overflow-hidden rounded-lg border'>
										<AnchorProductTable />
									</div>
								</>
							)}
						</FormProvider>
					</ModalBody>
					<ModalFooter>
						{canGoToNextStep && (
							<ModalClose asChild>
								<Button className='mr-auto' onClick={close}>
									{t('general_cancel')}
								</Button>
							</ModalClose>
						)}
						{canGoToPrevStep && (
							<Button onClick={goToPrevStep}>
								<ChevronLeftIcon />
								{t('general_back')}
							</Button>
						)}
						{canGoToNextStep && (
							<Button
								variant='primary'
								disabled={
									(currentStep === 1 && !methods.watch('product')?.sku) ||
									!methods.watch('anchor_type') ||
									(currentStep === 2 &&
										(!methods.watch('anchorProduct') ||
											!methods.watch('mainProduct')))
								}
								onClick={goToNextStep}
							>
								{t('general_next')}
								<ChevronRightIcon />
							</Button>
						)}
						{!canGoToNextStep && (
							<Button
								data-testid='modal-add-sku-to-anchor-management-save-button'
								variant='primary'
								onClick={handleSubmit}
							>
								{t('general_save')}
							</Button>
						)}
					</ModalFooter>
				</ErrorBoundary>
			</ModalContent>
		</Modal>
	)
}

function MainProductSelector() {
	const { t } = useTranslation()

	const { control, watch } = useFormContext()
	const [searchText, setSearchText] = useState('')
	const [debouncedSearchText] = useDebounceValue(searchText, 500)
	const {
		data: salesEntityProductCatalog,
		isFetching: isSalesEntityProductCatalogFetching,
	} = useAnchorsProductCatalogQuery<ProductCatalogQueryResponseModel>(
		{
			filters: [
				[
					{
						name: 'sales_entity_id',
						operation: Operators.IS as const,
						value: watch('anchorProduct.sales_entity_id'),
					},
				],
			] as FilterRuleModel[][],
			limit: 50,
			order_by: [{ name: 'sku', direction: 'asc' as const }],
			search: debouncedSearchText || undefined,
		},
		{
			enabled: !!watch('anchorProduct.sales_entity_id'),
		}
	)
	const salesEntityProductCatalogOptions = useMemo(
		() =>
			salesEntityProductCatalog?.data
				?.filter((item) => item.sku !== watch('product.sku'))
				.map((item) => ({
					value: item.sku,
					label: `${item.sku} ${item.title}`,
				})) || [],
		[salesEntityProductCatalog?.data]
	)

	return (
		<FormField
			name='mainProduct'
			control={control}
			render={({ field: { onChange, value }, fieldState: { error } }) => (
				<FormItem>
					<FormLabel>
						{t('anchoring_management.modal.select_sku.title')}
					</FormLabel>
					<FormControl>
						<AsyncCombobox
							data-testid='modal-assign-to-anchor-management-select-anchor-product'
							error={error}
							disabled={!watch('anchorProduct.sales_entity_id')}
							className='w-full'
							value={value ? value.title : ''}
							options={salesEntityProductCatalogOptions}
							placeholder={t('products_search')}
							onSearchChange={setSearchText}
							setValue={(value) => {
								const mainProduct =
									salesEntityProductCatalog?.data?.find(
										({ sku }) => sku === value
									) || null
								onChange(mainProduct)
							}}
							isLoading={isSalesEntityProductCatalogFetching}
							onClear={() => {
								setSearchText('')
								onChange(null)
							}}
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}

function SalesEntitySelector() {
	const { t } = useTranslation()

	const { control, watch, setValue } = useFormContext()
	const { data: productCatalog } =
		useAnchorsProductCatalogQuery<ProductCatalogQueryResponseModel>(
			{
				filters: [
					[
						{
							name: 'sku',
							operation: Operators.IS as const,
							value: watch('product.sku'),
						},
					],
				] as FilterRuleModel[][],
				limit: 50,
				order_by: [{ name: 'sku', direction: 'asc' as const }],
			},
			{
				enabled: !!watch('product.sku'),
			}
		)
	const {
		data: salesEntityIdsOptions,
		isLoading: isSalesEntityIdsOptionsLoading,
	} = useSalesEntitiesQuery<DataOption[]>({
		enabled: !!productCatalog?.data,
		select: pipe(
			(data) => {
				const enabledSalesEntityIds =
					map(prop('sales_entity_id'))(productCatalog?.data) || []

				return data?.sales_entities?.filter((item: SalesEntityModel) =>
					enabledSalesEntityIds.includes(item.id)
				)
			},
			map(paths(['id', 'name'])),
			toOption
		),
	})

	return (
		<FormField
			name='anchorProduct'
			control={control}
			render={({ field: { value, onChange, ref }, fieldState: { error } }) => (
				<FormItem>
					<FormLabel>{t('sales_unit')}</FormLabel>
					<Select
						onValueChange={(id) => {
							onChange({
								...productCatalog?.data?.find(
									(item) => item.sales_entity_id === id
								),
								sales_entity_name:
									salesEntityIdsOptions?.find((item) => item.value === id)
										?.label || '',
							})
							setValue('mainProduct', null)
						}}
						value={value?.sales_entity_id}
					>
						<FormControl data-name='anchorProduct'>
							<SelectTrigger
								ref={ref}
								aria-invalid={!!error}
								className='w-full'
							>
								<SelectValue
									className='w-full'
									placeholder={t(
										'anchoring_management.assign_to_modal.sales_entity_id.placeholder',
										{ defaultValue: 'Select business unit' }
									)}
								/>
							</SelectTrigger>
						</FormControl>
						<SelectContent>
							<SelectGroup>
								{isSalesEntityIdsOptionsLoading ? (
									<Skeleton className='h-6 w-full' />
								) : (
									salesEntityIdsOptions?.map((option) => (
										<SelectItem
											key={option.value}
											value={option.value}
											data-value={option.value}
										>
											{option.label}
										</SelectItem>
									))
								)}
							</SelectGroup>
						</SelectContent>
					</Select>
				</FormItem>
			)}
		/>
	)
}

function ProductSelector() {
	const { t } = useTranslation()

	const { control } = useFormContext()
	const [searchText, setSearchText] = useState('')
	const [debouncedSearchText] = useDebounceValue(searchText, 500)
	const { data: assortmentList, isFetching: isAssortmentListFetching } =
		useAssortmentListQuery(
			{
				limit: 50,
				order_by: [{ name: 'sku', direction: 'asc' as const }],
				search: debouncedSearchText || undefined,
			},
			{
				select: (data) =>
					uniqBy(prop('sku'), linesToProducts(data?.data) || []),
			}
		)
	const assortmentOptions = useMemo(
		() =>
			assortmentList?.map((item) => ({
				value: item.sku,
				label: `${item.sku} ${item.title}`,
			})) || [],
		[assortmentList]
	)

	return (
		<FormField
			name='product'
			control={control}
			render={({ field: { onChange, value }, fieldState: { error } }) => (
				<FormItem>
					<FormLabel>
						{t('anchoring_management.modal.select_sku.title')}
					</FormLabel>
					<FormControl>
						<AsyncCombobox
							error={error}
							className='w-full'
							data-testid='modal-add-sku-to-anchor-management-search-product'
							value={value ? value.title : ''}
							options={assortmentOptions}
							placeholder={t('products_search')}
							onSearchChange={setSearchText}
							setValue={(value) => {
								const product = assortmentOptions.find(
									(item) => item.value === value
								)!
								onChange({ sku: value, title: product.label })
							}}
							isLoading={isAssortmentListFetching}
							onClear={() => {
								onChange({ sku: '', title: '' })
								setSearchText('')
							}}
						/>
					</FormControl>
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
